import React, { useState } from 'react';
import { Box, TextField, IconButton, Tooltip, SxProps, Theme, Typography, Stack } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';

interface LinkBoxProps {
    link: string;
    placeholder?: string;
    sx?: SxProps<Theme>;
    onCopySuccess?: () => void;
    onCopyError?: (error: Error) => void;
    customComponent?: JSX.Element;
    /** CastingNoir-style tinted card (technical profile column) */
    variant?: 'technical';
}

const LinkBox: React.FC<LinkBoxProps> = ({
    link = 'https://example.com/shared-document',
    placeholder = 'Link will appear here...',
    sx = {},
    onCopySuccess,
    onCopyError,
    customComponent,
    variant,
}) => {
    const [copied, setCopied] = useState(false);
    const { themeStyle } = useThemeContext();

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            onCopySuccess?.();

            // Reset the copied state after 2 seconds
            setTimeout(() => {
                setCopied(false);
            }, 2000);
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to copy link');
            onCopyError?.(error);
        }
    };

    const isTechnical = variant === 'technical';

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                backgroundColor: isTechnical ? 'rgba(250,175,63,0.06)' : '#1a1a1a',
                border: isTechnical ? '1px solid rgba(250,175,63,0.22)' : '1px solid rgba(255,255,255,0.06)',
                p: 2.25,
                borderRadius: isTechnical ? '16px' : '14px',
                my: isTechnical ? 0 : 1.5,
                ...sx,
            }}>
            <Stack pb={2} direction={'row'} justifyContent={'space-between'}>
                <Stack>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: theme[themeStyle].highlight,
                            textTransform: 'uppercase',
                            letterSpacing: isTechnical ? '0.14em' : '0.08em',
                            fontSize: isTechnical ? 10 : 11,
                            fontWeight: 700,
                        }}>
                        Share Link
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].darkGray,
                            fontSize: 12,
                        }}>
                        Copy the link below to share with others
                    </Typography>
                </Stack>
                {customComponent && customComponent}
            </Stack>
            <TextField
                value={link}
                placeholder={placeholder}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                            <IconButton
                                onClick={handleCopy}
                                disabled={!link}
                                size="small"
                                sx={{
                                    color: copied ? theme[themeStyle].black : theme[themeStyle].black,
                                    backgroundColor: theme[themeStyle].highlight,
                                    borderRadius: '8px',
                                    '&:hover': {
                                        bgcolor: theme[themeStyle].primary_orange,
                                        color: theme[themeStyle].black,
                                    },
                                    '&:disabled': {
                                        color: theme[themeStyle].placeholderGray,
                                    },
                                    transition: 'all 0.2s ease-in-out',
                                    padding: 1,
                                }}>
                                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    ),
                    sx: {
                        bgcolor: '#131313',
                        color: theme[themeStyle].darkGray,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'rgba(255,255,255,0.08)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme[themeStyle].highlight,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme[themeStyle].highlight,
                        },
                        '& .MuiInputBase-input': {
                            color: theme[themeStyle].darkGray,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 14,
                            '&::placeholder': {
                                color: theme[themeStyle].placeholderGray,
                                opacity: 1,
                            },
                        },
                    },
                }}
                sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                        borderRadius: 2,
                    },
                }}
            />
        </Box>
    );
};

export { LinkBox };
